import React from 'react'
import { Switch, Route, BrowserRouter, Redirect } from 'react-router-dom'
import { useSelector } from 'react-redux'

import SignedInLayout from 'layouts/signedIn'

import Login from 'pages/Login'
import Home from 'pages/Home'
import Forbidden from 'pages/Forbidden'
import Collaborators from 'pages/Collaborators'
import ManualAnalysisAudit from 'pages/ManualAnalysisAudit'
import Statistics from 'pages/Statistics'
import UserActivityLogMonitor from 'pages/UserActivityLogMonitor'

import { ROUTE_CONSTANTS } from 'utils/constants/routeConstants'
import { USER_ROLE } from 'utils/constants/application'

import UserManagement from 'pages/UserManagement'
import SecuredRoute from './securedRoute'
import ConfigOptions from "../pages/ConfigOptions"
import OpenRouteWithLayout from "./OpenRouteWithLayout"
import VerifyEmailSignature from "../pages/PasswordManagement/VerifyEmailSignature"
import { EmailSent, PasswordCreation, PasswordRecover, PasswordSuccess } from "../pages/PasswordManagement"
import DefaultLayout from "../layouts/Default"
import { readOptionsManagementScopes, Scopes } from "../utils/authUtils"

const withLoginRoutes = (isAccessManager) => (<Switch>
  <Route path="/login" exact component={Login} />

  {isAccessManager ? (
    <Route
      path={ROUTE_CONSTANTS.HOME}
      exact
      render={() => <Redirect to={ROUTE_CONSTANTS.USER_ACTIVITY} />}
    />
  ) : (
    <SecuredRoute
      path={ROUTE_CONSTANTS.HOME}
      requiredScopes={[Scopes.ACCESS_PANEL]}
      exact
      parent={SignedInLayout}
      child={Home}
    />
  )}

  <SecuredRoute
    path={ROUTE_CONSTANTS.MANUAL_ANALISYS_AUDIT}
    requiredScopes={[Scopes.ACCESS_PANEL]}
    exact
    parent={SignedInLayout}
    child={ManualAnalysisAudit}
  />

  <SecuredRoute
    path={ROUTE_CONSTANTS.STATISTICS}
    requiredScopes={[Scopes.ACCESS_PANEL]}
    exact
    parent={SignedInLayout}
    child={Statistics}
  />

  <SecuredRoute
    path={ROUTE_CONSTANTS.USER_ACTIVITY}
    requiredScopes={[Scopes.USER_ACTIVITY_LIST_LOGS]}
    exact
    parent={SignedInLayout}
    child={UserActivityLogMonitor}
  />

  <SecuredRoute
    path={ROUTE_CONSTANTS.USER_MANAGEMENT}
    requiredScopes={[Scopes.USER_MANAGEMENT_LIST_USERS]}
    exact
    parent={SignedInLayout}
    child={UserManagement}
  />

<SecuredRoute
    path={ROUTE_CONSTANTS.RESTRICTED_OPTIONS}
    requiredScopes={readOptionsManagementScopes}
    exact
    parent={SignedInLayout}
    child={ConfigOptions}
  />


  <SecuredRoute
    path={ROUTE_CONSTANTS.COLLABORATORS}
    requiredScopes={[Scopes.ACCESS_PANEL]}
    exact
    parent={SignedInLayout}
    child={Collaborators}
  />

  <Route
    path={ROUTE_CONSTANTS.PASSWORD_RECOVER}
    exact
    component={VerifyEmailSignature}
  />

  <OpenRouteWithLayout
    parent={DefaultLayout}
    child={PasswordRecover}
    exact
    path={ROUTE_CONSTANTS.FORGOT_PASSWORD}
  />

  <SecuredRoute
    requiredScopes={[Scopes.PASSWORD_CHANGE]}
    parent={DefaultLayout}
    child={PasswordCreation}
    exact
    path={ROUTE_CONSTANTS.PASSWORD_CREATION}
  />

  <SecuredRoute
    requiredScopes={[Scopes.PASSWORD_CHANGE]}
    parent={DefaultLayout}
    child={PasswordCreation}
    exact
    path={ROUTE_CONSTANTS.PASSWORD_REDEFINE}
  />

  <OpenRouteWithLayout
    parent={DefaultLayout}
    child={EmailSent}
    exact
    path={ROUTE_CONSTANTS.EMAIL_SENT}
  />

  <OpenRouteWithLayout
    parent={DefaultLayout}
    child={PasswordSuccess}
    exact
    path={ROUTE_CONSTANTS.PASSWORD_SUCCESS}
  />

  <OpenRouteWithLayout
    parent={DefaultLayout}
    child={Forbidden}
    exact
    path={ROUTE_CONSTANTS.FORBIDDEN}
  />
</Switch>)

const withoutLoginRoutes = (isAccessManager) => (<Switch>
  {isAccessManager ? (
    <Route
      path={ROUTE_CONSTANTS.HOME}
      exact
      render={() => <Redirect to={ROUTE_CONSTANTS.USER_ACTIVITY} />}
    />
  ) : (
    <Route
      path={ROUTE_CONSTANTS.HOME}
      exact
      component={(rest) => <SignedInLayout component={Home} {...rest} />}
    />
  )}

  <Route
    path={ROUTE_CONSTANTS.COLLABORATORS}
    exact
    component={(rest) => <SignedInLayout component={Collaborators} {...rest} />}
  />

  <Route
    path={ROUTE_CONSTANTS.USER_ACTIVITY}
    exact
    component={(rest) => <SignedInLayout component={UserActivityLogMonitor} {...rest} />}
  />
</Switch>)

const Router = () => {
  const userRole = useSelector(state => state.auth.user_role)
  const isAccessManager = userRole === USER_ROLE.accessManager

  return (<BrowserRouter>
    {process.env.REACT_APP_LOGIN_DISABLED === 'true'
      ? withoutLoginRoutes(isAccessManager)
      : withLoginRoutes(isAccessManager)}
  </BrowserRouter>)
}

export default Router
